import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Select, Typography, Row, Col, Form, DatePicker, Input, Checkbox, Radio, Upload, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { REGIONS } from '../../config/constants/DropDownForms';
import { COMMERCIAL_BUSINESS } from '../../config/constants/DropDownForms';
import moment from 'moment';
import 'moment/locale/es-mx';
import locale from 'antd/es/date-picker/locale/es_ES';
import { SET_PREVIEW_DATA } from '../../state/lease_contract/types';
import rutValidator from '../../helpers/rutValidator';
import { validateRutUsed } from '../../helpers/forms';
import { SET_USED_RUTS } from '../../state/forms/types';
import PropTypes from 'prop-types';

const { Option, OptGroup } = Select;
const { Title, Text } = Typography;

const LegalPerson = ({ formName, previewData, setPreview, id, contractFormData, usedRuts, setUsedRuts }) => {

  const [formHasNotary, setFormHasNotary] = useState([false]);
  const [showUpdateDocumentCheckbox, setShowDocumentCheckbox] = useState([false]);
  const [showUploadDocument, setShowUploadDocument] = useState([false]);

  useEffect(() => {
    if (contractFormData.editForm) {
      let initialState = contractFormData[id] && contractFormData[id].map(signer => signer.powerCert ? true : false);
      setShowUploadDocument(initialState);
      setShowDocumentCheckbox(initialState);
      setFormHasNotary(contractFormData[id] && contractFormData[id].map(signer => signer.hasLegalPerson ? !signer.companyCreationMethod : false));
    }
    return () => {
      setShowDocumentCheckbox(element => { delete element[formName]; return [...element]; });
      setShowUploadDocument(element => { delete element[formName]; return [...element]; });
      delete usedRuts[`${id}_${formName}_companyRol`];
      setUsedRuts(usedRuts);
    };
  }, [contractFormData]);

  const savePreview = (data) => {
    setPreview({
      ...previewData,
      ...data
    });
  };

  const disabledDate = current => {
    return current && current > moment().endOf('day');
  };

  return (
    <React.Fragment>
      <Row className="mb-2">
        <Col span={24}>
          <Title level={5}>Datos Empresa</Title>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} className="d-flex mb-2">
          <Title level={5} className="mb-0 mr-1 fw-medium">¿Cómo creaste la empresa?</Title>
        </Col>
        <Col>
          <Form.Item
            name={[formName, 'companyCreationMethod']}
            rules={[{ required: true, message: 'Debes seleccionar una opción' }]}
          >
            <Radio.Group
              onChange={(option) => {
                setFormHasNotary(element => { element[formName] = !option.target.value; return [...element]; });
                previewData[id][formName].companyCreationMethod = option.target.value;
                savePreview(previewData);
              }}
            >
              <Radio className="fw-medium" value={false}>Forma tradicional por escritura pública</Radio>
              <Radio className="fw-medium" value={true}>Por el sitio web {'"Tu empresa en un día"'}</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} md={12} className="position-relative">
          <Form.Item
            name={[formName, 'businessName']}
            label="Razón social"
            rules={[{ required: true, message: 'Debe ingresar nombre' }]}
            className="position-relative"
          >
            <Input
              size="large"
              onBlur={(event) => {
                previewData[id][formName].businessName = event.target.value;
                savePreview(previewData);
              }}
              autoComplete="off"
              maxLength={100}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12} className="position-relative">
          <Form.Item
            label='Giro'
            name={[formName, 'giro']}
            rules={[{ required: true, message: 'Debes seleccionar un giro' }]}
          >
            <Select size="large" showSearch style={{ width: '100%' }} placeholder="Seleccionar" initialvalues='Sin giro'
              onSelect={(value) => {
                previewData[id][formName].giro = value;
                savePreview(previewData);
              }}
            >
              <Option value='Sin giro'>Sin giro</Option>
              {COMMERCIAL_BUSINESS.map((giro, key) => (
                <OptGroup key={key} label={giro.nombre}>
                  {giro.giros.map((nombreGiro, key) => (
                    <Option key={key} value={nombreGiro}>{nombreGiro}</Option>
                  ))}
                </OptGroup>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            className="mb-0"
            label="RUT"
            name={[formName, 'companyRol']}
            preserve={false}
            rules={[
              ({
                validator(_, value) {
                  if (value && rutValidator(value)) {
                    if (validateRutUsed(usedRuts, value, `${id}_${formName}_companyRol`)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      'Rut ya fue ingresado',
                    );
                  }
                  return Promise.reject(
                    'Debe ingresar un rut válido',
                  );
                },
              }),
              {
                required: true,
                pattern: new RegExp(/^[0-9]+[-|‐]{1}[0-9kK]{1}$/),
                message: 'Debe ingresar rol con guión'
              },
            ]}
          >
            <Input
              size="large"
              className="mb-1"
              onBlur={(event) => {
                previewData[id][formName].companyRol = event.target.value;
                usedRuts[event.target.id] = event.target.value;
                savePreview(previewData);
                setUsedRuts(usedRuts);
              }}
            />
          </Form.Item>
          <Text type="secondary" className="small mb-4 d-block">Ej: 15236987-4</Text>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label="Dirección"
            name={[formName, 'address']}
            rules={[{ required: true, message: 'Debes ingresar una dirección' }]}
            className="mb-1"
          >
            <Input
              size="large"
              autoComplete="off"
              onBlur={(event) => {
                previewData[id][formName].address = event.target.value;
                savePreview(previewData);
              }}
              maxLength={100}
            />
          </Form.Item>
          <Text type="secondary" className="small d-block mb-4">Ej: Avenida Libertad 354 Depto 1203</Text>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name={[formName, 'commune']}
            label="Comuna"
            rules={[{ required: true, message: 'Debes seleccionar una opción' }]}
          >
            <Select size="large" showSearch style={{ width: '100%' }} placeholder="Ingresar o seleccionar"
              onSelect={(value) => {
                previewData[id][formName].commune = value;
                savePreview(previewData);
              }}
            >
              {REGIONS.map((region, key) => (
                <OptGroup key={key} label={region.region}>
                  {region.comunas.map((comuna, key) => (
                    <Option key={key} value={`${comuna}, ${region.region}`}>{comuna}</Option>
                  ))}
                </OptGroup>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} md={12} className="position-relative">
          <div className="ant-col ant-form-item-label position-relative d-flex align-items-center">
            <label className="ant-form-item-required mr-1">Fecha de {formHasNotary[formName] ? 'poder' : 'registro en "Tu empresa en un día"'}</label>
          </div>
          <Form.Item
            name={[formName, 'dateOfconstitution']}
            rules={[{ required: true, message: 'Debes seleccionar una fecha' }]}
          >
            <DatePicker
              disabledDate={disabledDate}
              locale={locale}
              format="DD-MM-YYYY"
              style={{ width: '100%' }}
              placeholder="Seleccionar"
              size="large"
              onChange={(value) => {
                previewData[id][formName].dateOfconstitution = new Date(value).toISOString();
                savePreview(previewData);
              }}
            />
          </Form.Item>
        </Col>
        {formHasNotary[formName] &&
          <Col xs={24} md={12} className="position-relative mb-4">
            <Form.Item
              name={[formName, 'constitutionNotary']}
              label="Nombre notaría de poder"
              rules={[{ required: true, message: 'Debe ingresar nombre de notario' }]}
              className="position-relative"
            >
              <Input
                size="large"
                onBlur={(event) => {
                  previewData[id][formName].constitutionNotary = event.target.value;
                  savePreview(previewData);
                }}
                autoComplete="off"
                maxLength={100}
              />
            </Form.Item>
          </Col>
        }
        {showUpdateDocumentCheckbox[formName] &&
          <Row className="mb-4">
            <Col xs={24}>
              <Checkbox onChange={(value) => { setShowUploadDocument(element => { element[formName] = !value.target.checked; return [...element]; }); }}>¿Desea cargar un nuevo certificado?</Checkbox>
            </Col>
          </Row>
        }
        {!showUploadDocument[formName] &&
          <Col xs={24} md={12} className="position-relative mb-4">
            <Form.Item
              name={[formName, 'certificateFile']}
              label={`Adjuntar certificado de ${formHasNotary[formName] ? 'poder' : '"Tu empresa en un día"'}`}
              rules={[
                ({
                  validator(_, value) {
                    if (value && value.fileList && value.fileList[0]) {
                      if (value.file.size > 4000000) {
                        return Promise.reject(
                          'El PDF excede tamaño máximo de 4MB.',
                        );
                      }
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      'Debe cargar el certificado en formato PDF',
                    );
                  },
                  required: true,
                })
              ]}
              className="position-relative"
            >
              <Upload
                accept='application/pdf'
                customRequest={({ onSuccess }) => { setTimeout(() => { onSuccess('ok'); }, 0); }}
                multiple={false}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Subir archivo</Button>
              </Upload>
            </Form.Item>
            <Text type="secondary" className="small d-block">El PDF no debe ser mayor a 4MB</Text>
          </Col>
        }
      </Row>
    </React.Fragment>
  );
};

LegalPerson.propTypes = {
  previewData: PropTypes.object.isRequired,
  setPreview: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  contractFormData: PropTypes.object.isRequired,
  usedRuts: PropTypes.object.isRequired,
  setUsedRuts: PropTypes.func.isRequired,
  formName: PropTypes.string.isRequired,
};

const data = (state) => ({
  previewData: state.contract.previewData,
  contractFormData: state.contract.contractFormData,
  usedRuts: state.forms.usedRuts,
});

const actions = (dispatch) => ({
  setPreview: (data) => dispatch({ type: SET_PREVIEW_DATA, payload: { ...data } }),
  setUsedRuts: (data) => dispatch({ type: SET_USED_RUTS, payload: { ...data } }),
});

export default connect(data, actions)(LegalPerson);